/**
 * CancellablePromise is a chaining promise that can be cancelled; any promises that are built off it can also be cancelled
 */
export class CancellablePromise<T> implements Promise<T> {
    constructor(
        private underlying: Promise<T>,
        public cancel: () => void
    ) {}

    then<TResult1 = T, TResult2 = never>(
        onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any is part of the promise signature
        onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
    ): CancellablePromise<TResult1 | TResult2> {
        return new CancellablePromise(this.underlying.then(onfulfilled, onrejected), this.cancel)
    }

    catch<TResult = never>(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any is part of the promise signature
        onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null
    ): CancellablePromise<T | TResult> {
        return new CancellablePromise(this.underlying.catch(onrejected), this.cancel)
    }

    finally(onfinally?: (() => void) | null | undefined): CancellablePromise<T> {
        return new CancellablePromise(this.underlying.finally(onfinally), this.cancel)
    }

    [Symbol.toStringTag] = "CancellablePromise"
}

/**
 * Wait a given amount of time before continuing
 * @param timeout time to wait in milliseconds
 * @returns Promise the resolves after the given amount of time
 */
export const wait = (timeout: number | undefined) => {
    let t: ReturnType<typeof setTimeout>
    const p = new Promise<void>((resolve) => {
        t = setTimeout(() => resolve(), timeout)
    })
    return new CancellablePromise(p, () => clearTimeout(t))
}

/**
 * Polls an async predicate function at a fixed interval until it returns a non-null/undefined
 * value, or the maximum number of retries is exhausted.
 */
export async function pollUntil<T>(
    fn: () => Promise<T | null | undefined>,
    intervalMs: number,
    maxRetries: number
): Promise<T | null | undefined> {
    for (let i = 0; i < maxRetries; i++) {
        await wait(intervalMs)
        const result = (await fn()) ?? undefined
        if (result !== undefined) return result
    }
    return null
}

/**
 * Like `asyncConcurrency`, but uses a sliding window instead of fixed batches.
 * As soon as any in-flight promise settles, the next item is picked up immediately,
 * keeping up to `concurrency` promises running at all times.
 *
 * @param items the set of items to be operated on
 * @param concurrency the max number of concurrent in-flight promises
 * @param asyncFn the function to run on each item
 * @returns the results in the same order as the input items
 */
export async function asyncConcurrencyWindow<T, R = T>(
    items: T[],
    concurrency: number,
    asyncFn: (item: T) => Promise<R>
): Promise<R[]> {
    const results = new Array<R>(items.length)
    let nextIndex = 0

    const runNext = async (): Promise<void> => {
        const i = nextIndex++
        if (i >= items.length) return
        results[i] = await asyncFn(items[i])
        await runNext()
    }

    const initialWorkers = Math.min(concurrency, items.length)
    const workers: Promise<void>[] = []
    for (let i = 0; i < initialWorkers; i++) {
        workers.push(runNext())
    }

    await Promise.all(workers)
    return results
}

/**
 * Processes items in fixed-size chunks sequentially, with a configurable delay between chunks.
 * Within each chunk items are processed concurrently. Useful for smoothing load when fanning
 * out to downstream services (e.g. Lambda invocations spread across many orgs).
 *
 * @param items     All items to process
 * @param chunkSize Number of items per chunk (processed concurrently within each chunk)
 * @param delayMs   Milliseconds to wait between chunks
 * @param fn        Async function to run on each item
 */
export async function processStaggeredChunks<T>(
    items: T[],
    chunkSize: number,
    delayMs: number,
    fn: (item: T) => Promise<void>
): Promise<void> {
    const totalChunks = Math.ceil(items.length / chunkSize)

    for (let i = 0; i < totalChunks; i += 1) {
        const startIndex = i * chunkSize
        const chunkItems = items.slice(startIndex, startIndex + chunkSize)
        await Promise.all(chunkItems.map(async (chunkItem) => fn(chunkItem)))

        if (i + 1 < totalChunks) {
            await new Promise<void>((resolve) => setTimeout(resolve, delayMs))
        }
    }
}

export async function asyncConcurrency<T, R = T>(
    items: T[],
    concurrency: number,
    asyncFn: (item: T) => Promise<R>,
    failFast: true
): Promise<R[]>
export async function asyncConcurrency<T, R = T>(
    items: T[],
    concurrency: number,
    asyncFn: (item: T) => Promise<R>,
    failFast?: false
): Promise<(R | { failed: true; err: unknown })[]>
/**
 *
 * Sets up worker functions to be run on an array of items. Ensures only a set amount of requests are run at a time
 *
 * @param items the set of items to be operated on
 * @param concurrency the max amount of workers made
 * @param asyncFn the function for the workers to run, which takes a single item as an argument
 * @param failFast if true, the first error is rethrown immediately (like Promise.all). Otherwise, errors are captured as `{ failed: true, err }` like Promise.allSettled
 * @returns the new array with the items after being run through the asyncFn
 */
export async function asyncConcurrency<T, R = T>(
    items: T[],
    concurrency: number,
    asyncFn: (item: T) => Promise<R>,
    failFast = false
): Promise<(R | { failed: true; err: unknown })[]> {
    const results = new Array(items.length)
    let currentIndex = 0

    async function worker() {
        while (true) {
            const i = currentIndex++
            if (i >= items.length) break // No more items

            // Call the async function and store the result
            results[i] = await asyncFn(items[i]).catch((err) => {
                if (failFast) throw err
                return { failed: true, err }
            })
        }
    }

    // Create the workers
    const workers: Promise<void>[] = []
    for (let w = 0; w < concurrency; w++) {
        workers.push(worker())
    }

    // Start the workers
    await Promise.all(workers)
    return results
}
